import {
  OrderAggregateDetail,
  OrderPaymentProviderSnapshot,
  OrderRefundCapability,
  OrderRefundItem,
  OrderSettlementSummary
} from './order';

export function mapOrderRecord(record: Record<string, unknown>, aliases: Record<string, string>): Record<string, unknown> {
  const mapped = { ...record };
  Object.keys(aliases).forEach(source => {
    if (Object.prototype.hasOwnProperty.call(record, source)) {
      mapped[aliases[source]] = record[source];
    }
  });
  return mapped;
}

export const ORDER_RESOURCE_ALIASES: Record<string, Record<string, string>> = {
  orderDetail: {
    id: 'od_id',
    order_id: 'od_orders',
    type: 'od_type',
    source_table: 'od_source_table',
    source_code: 'od_source_code',
    name: 'od_name',
    item_code: 'od_item_code',
    per_unit_price: 'od_per_unit_price',
    unit: 'od_unit',
    total: 'od_total',
    refund_count: 'od_refund_count',
    cash: 'od_cash',
    payment_cash: 'od_payment_cash',
    actual_cash: 'od_actual_cash',
    refund_cash: 'od_refund_cash',
    state: 'od_state',
    create_time: 'od_create_time'
  },
  paymentOrder: {
    id: 'p_id',
    code: 'p_code',
    corporation: 'p_corporation',
    pharmacy: 'p_pharmacy',
    distributor: 'p_distributor',
    hospital: 'p_hospital',
    patient: 'p_patient',
    cash: 'p_cash',
    payment_cash: 'p_payment_cash',
    actual_cash: 'p_actual_cash',
    refund_cash: 'p_refund_cash',
    refundable_cash: 'p_refundable_cash',
    type: 'p_type',
    expire_time: 'p_expire_time',
    state: 'p_state',
    create_time: 'p_create_time'
  },
  refundDetail: {
    id: 'ord_id',
    payment_transaction: 'ord_payment_transaction',
    order_detail: 'ord_order_detail',
    count: 'ord_count',
    cash: 'ord_cash',
    create_time: 'ord_create_time'
  }
};

export function mapOrderAggregateDetail(data: Record<string, unknown>): OrderAggregateDetail {
  const refundCapability = mapRefundCapability(data['refund_capability']);
  const details = asRecordArray(data['details']).map(item => {
    const mapped = mapOrderRecord(item, ORDER_RESOURCE_ALIASES['orderDetail']);
    const detailId = mapped['od_id'];
    const refundItem = refundCapability.items.find(candidate => String(candidate.detailId) === String(detailId));
    if (refundItem) {
      mapped['refundable_count'] ??= refundItem.refundableCount;
      mapped['refundable_cash'] ??= refundItem.refundableCash;
    }
    return mapped;
  });

  const result: OrderAggregateDetail = {
    order: asRecord(data['order']),
    details,
    paymentOrders: asRecordArray(data['payment_orders']).map(item => mapOrderRecord(item, ORDER_RESOURCE_ALIASES['paymentOrder'])),
    paymentFlows: asRecordArray(data['payment_flows']),
    refundDetails: asRecordArray(data['refund_details']).map(item => mapOrderRecord(item, ORDER_RESOURCE_ALIASES['refundDetail'])),
    refundCapability
  };

  const provider = mapProviderSnapshot(data['provider']);
  if (provider) result.provider = provider;
  const settlementSummary = mapSettlementSummary(data['settlement_summary']);
  if (settlementSummary) result.settlementSummary = settlementSummary;
  return result;
}

export function paymentFlowStatusText(record: Record<string, unknown>): string {
  const stateName = record['state_name'];
  if (typeof stateName === 'string' || typeof stateName === 'number') {
    const text = String(stateName).trim();
    if (text) return text;
  }

  const rawState = record['state'];
  if (rawState === null || rawState === undefined || rawState === '') return '-';

  const state = Number(rawState);
  if (record['operation_pending'] === true || state === 1) return '处理中';
  switch (state) {
    case 0:
      return '初始化';
    case 2:
      return '成功';
    case 4:
      return '失败';
    case 8:
      return '忽略';
    case 9:
      return '已关闭';
    case 99:
      return '异常';
    default:
      return `${String(rawState)}-`;
  }
}

export function paymentFlowResultText(record: Record<string, unknown>): string {
  const state = Number(record['state']);
  if (record['operation_pending'] === true || state === 1) return '等待支付通道结果';
  if (state === 2) return '-';

  const failureReason = record['failure_reason'];
  if (typeof failureReason === 'string' || typeof failureReason === 'number') {
    const text = String(failureReason).trim();
    if (text) return text;
  }
  if (state === 4) return '交易流水处理失败';
  if (state === 9) return '交易流水已关闭';
  if (state === 99) return '交易流水状态异常';
  return '未识别的交易流水状态，请人工核实';
}

export function formatOrderDateTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = typeof value === 'number' || /^\d+$/.test(String(value)) ? Number(value) : NaN;
  const date = Number.isFinite(numeric) ? new Date(numeric > 9999999999 ? numeric : numeric * 1000) : new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (part: number): string => `${part}`.padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}:${pad(date.getSeconds())}`;
}

function mapRefundCapability(value: unknown): OrderRefundCapability {
  const record = asRecord(value);
  const items: OrderRefundItem[] = asRecordArray(record['items']).map(item => {
    const rawDetailId = item['detail_id'] ?? item['id'];
    return {
      detailId: typeof rawDetailId === 'string' || typeof rawDetailId === 'number' ? rawDetailId : '',
      detailType: scalar(item['detail_type']),
      name: scalar(item['name']),
      refundableCount: scalar(item['refundable_count'], '0'),
      refundableCash: scalar(item['refundable_cash'], '0')
    };
  });
  return {
    supported: record['supported'] === true,
    reason: scalar(record['reason']),
    items
  };
}

function mapProviderSnapshot(value: unknown): OrderPaymentProviderSnapshot | undefined {
  if (!isRecord(value)) return undefined;
  return {
    providerName: scalar(value['provider_name']),
    applicationName: scalar(value['application_name']),
    applicationCode: scalar(value['application_code']),
    driverSummary: scalar(value['driver_summary']) || undefined
  };
}

function mapSettlementSummary(value: unknown): OrderSettlementSummary | undefined {
  if (!isRecord(value)) return undefined;
  const totalCash = scalar(value['total_cash']);
  return {
    settlementCode: scalar(value['settlement_code']) || undefined,
    status: scalar(value['status']) || undefined,
    cash: scalar(value['cash']) || undefined,
    totalCash: totalCash || undefined,
    medicalInsuranceCash: scalar(value['medical_insurance_cash']) || undefined,
    personalAccountCash: scalar(value['personal_account_cash']) || undefined,
    selfPayCash: scalar(value['self_pay_cash']) || undefined,
    remark: scalar(value['remark']) || undefined,
    // 原始报文只在管理端接口返回，按快照原样保留，不裁剪第三方协议键名与嵌套结构。
    rawExtend: isRecord(value['raw_extend']) ? value['raw_extend'] : undefined
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function asRecordArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function scalar(value: unknown, fallback = ''): string {
  return value === null || value === undefined ? fallback : String(value);
}
